import type {
  ApiReturnProject,
  ApiReturnSite,
  ReportElementType,
} from '@highimpact-frontend/api-types';
import type { ReportExportMode } from '@highimpact/dashboard';
import {
  GridComponent,
  flattenExportableElements,
  preprocessStructure,
  useDashboardHost,
  useDashboardLibrarySelector,
  useDashboardScope,
  useReportGather,
} from '@highimpact/dashboard';
import {
  HorizontalNavComponent,
  NavItemComponent,
  NavSectionComponent,
  PermissionDecoratorV2,
} from '@highimpact/dashboard-ui';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { forwardRef, useRef, useState, type LegacyRef } from 'react';

import SiteDecorator from '../pages/sites/utils/SiteDecorator';

type ReportProgressState =
  | {
      phase: 'gathering';
      completed: number;
      total: number;
      currentName?: string;
      mode: ReportExportMode;
    }
  | { phase: 'building'; completed: number; total: number }
  | null;

function ReportDownloadBar({
  grid_id,
  project,
  site,
}: {
  grid_id: number;
  project: ApiReturnProject;
  site: ApiReturnSite;
}) {
  const { api } = useDashboardHost();
  const { gatherReportExports, hasInteractiveReportExportsForElements } =
    useReportGather();
  const [pending, setPending] = useState<boolean>(false);
  const [modePickerOpen, setModePickerOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelContext, setCancelContext] = useState<{
    element: ReportElementType;
    displayName: string;
    resolve: (decision: 'proceed' | 'abort') => void;
  } | null>(null);
  const [progress, setProgress] = useState<ReportProgressState>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success';
  }>({ open: false, message: '', severity: 'error' });

  const grid = useDashboardLibrarySelector(
    (state) => state.dashboard.reports[grid_id]
  );

  if (!grid) {
    return null;
  }

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const structure = JSON.parse(JSON.stringify(grid.params));

  async function runReportBuild(mode: ReportExportMode) {
    setPending(true);
    abortControllerRef.current = new AbortController();

    const preprocessedStructure = {
      ...structure,
      main_template: {
        ...structure.main_template,
        elements: preprocessStructure(structure.main_template.elements),
      },
    };

    const exportableElements = flattenExportableElements(
      preprocessedStructure.main_template.elements
    );
    const total = exportableElements.length;

    setProgress({
      phase: 'gathering',
      completed: 0,
      total,
      mode,
    });

    try {
      const results = await gatherReportExports(
        preprocessedStructure.main_template.elements,
        mode,
        {
          signal: abortControllerRef.current.signal,
          onProgress: (event) => {
            if (event.phase === 'gathering') {
              setProgress({
                phase: 'gathering',
                completed: event.completedCount,
                total: event.totalCount,
                currentName: event.currentDisplayName,
                mode: event.mode,
              });
            }
          },
          onExportCancelled: (element, displayName) =>
            new Promise<'proceed' | 'abort'>((resolve) => {
              setCancelContext({ element, displayName, resolve });
              setCancelDialogOpen(true);
            }),
        }
      );

      results.forEach(({ element, result }) => {
        element.value = result as typeof element.value;
      });

      setProgress({ phase: 'building', completed: total, total });

      const blob = await api.report.buildReportDocx(preprocessedStructure);

      setPending(false);
      setProgress(null);

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = grid.name
        ? `${grid.name
            .replace(/_*-+_*/g, '_')
            .replace(/[\s-]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '')}.docx`
        : 'report.docx';
      anchor.click();
    } catch (error) {
      setPending(false);
      setProgress(null);
      if ((error as Error).message !== 'Report export cancelled by user') {
        const responseError = error as { response?: { status?: number } };
        let message =
          'Un problème est survenu lors de la génération du rapport. Veuillez réessayer plus tard.';
        if (responseError?.response?.status === 500) {
          message =
            'Erreur interne du service de génération de rapports. Veuillez réessayer plus tard.';
        } else if (responseError?.response?.status === 504) {
          message =
            'Le service de génération de rapports est indisponible. Veuillez réessayer plus tard.';
        }
        setSnackbar({
          open: true,
          message,
          severity: 'error',
        });
      }
    }
  }

  function handleGenerateClick() {
    const preprocessedElements = preprocessStructure(
      structure.main_template.elements
    );

    if (hasInteractiveReportExportsForElements(preprocessedElements)) {
      setModePickerOpen(true);
      return;
    }
    void runReportBuild('headless');
  }

  function handleModeSelected(mode: ReportExportMode) {
    setModePickerOpen(false);
    void runReportBuild(mode);
  }

  function handleCancelDialogClose(decision: 'proceed' | 'abort') {
    setCancelDialogOpen(false);
    cancelContext?.resolve(decision);
    setCancelContext(null);
  }

  const progressPercent =
    progress === null
      ? 0
      : progress.phase === 'building'
      ? 100
      : Math.round(
          (progress.completed / Math.max(progress.total + 1, 1)) * 100
        );

  const progressLabel =
    progress === null
      ? ''
      : progress.phase === 'building'
      ? 'Génération du rapport…'
      : progress.mode === 'interactive' && progress.currentName
      ? `Exportation des données… ${progress.completed}/${progress.total} widgets exportées (actuel: ${progress.currentName})`
      : `Exportation des données… ${progress.completed}/${progress.total} widgets exportées`;

  return (
    <>
      <HorizontalNavComponent side="bottom" id="nav-report">
        <NavSectionComponent>
          {progress !== null && (
            <NavItemComponent className="nav-item-static">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minWidth: 280,
                  maxWidth: 520,
                  pr: 2,
                }}
              >
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {progressLabel}
                </Typography>
                <LinearProgress
                  variant={
                    progress.phase === 'building'
                      ? 'indeterminate'
                      : 'determinate'
                  }
                  value={progressPercent}
                  sx={{ width: '100%' }}
                />
              </Box>
            </NavItemComponent>
          )}
        </NavSectionComponent>

        <NavSectionComponent>
          <NavItemComponent className="nav-item-static">
            <PermissionDecoratorV2
              item={'reports_generate_button'}
              project_id={Number(project.id)}
              site_id={Number(site.id)}
            >
              <button
                type="button"
                className="uk-button"
                onClick={handleGenerateClick}
                disabled={pending}
              >
                {pending ? 'téléchargement...' : 'Télécharger le rapport'}
              </button>
            </PermissionDecoratorV2>
          </NavItemComponent>
        </NavSectionComponent>
      </HorizontalNavComponent>

      <Dialog
        open={modePickerOpen}
        onClose={() => setModePickerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Éditer un rapport</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Veuillez sélectionner le mode d&apos;exportation :
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 240 }}>
              <CardActionArea onClick={() => handleModeSelected('headless')}>
                <CardContent>
                  <AutoModeIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6">Automatique</Typography>
                  <Typography variant="body2">
                    Export des données de tous les éléments du tableau de bord
                    dans leur mode par défaut (recommandé).
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
            <Card sx={{ flex: 1, minWidth: 240 }}>
              <CardActionArea onClick={() => handleModeSelected('interactive')}>
                <CardContent>
                  <TuneIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6">Interactif</Typography>
                  <Typography variant="body2">
                    Export selon vos paramètres personnalisés pour les éléments
                    qui supportent ce mode ; export par défaut pour les autres.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModePickerOpen(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => handleCancelDialogClose('abort')}
      >
        <DialogTitle>Export annulé</DialogTitle>
        <DialogContent>
          <Typography>
            L&apos;export du widget{' '}
            <strong>{cancelContext?.displayName}</strong> a été annulé. Que
            souhaitez-vous faire ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCancelDialogClose('abort')}>
            Annuler la génération
          </Button>
          <Button
            onClick={() => handleCancelDialogClose('proceed')}
            variant="contained"
          >
            Continuer sans cet export
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function SiteReportPage(_props: object, ref: LegacyRef<HTMLDivElement>) {
  const { grid_id } = useDashboardScope();

  if (grid_id === null) {
    return null;
  }

  return SiteDecorator(ref, (project, site) => (
    <>
      <GridComponent
        site={site}
        project={project}
        className="grid-report"
        draggableHandle=".react-grid-dragHandleExample"
        grid_type="reports"
        grid_id={grid_id}
        is_editable={true}
        dashboard_only_mode={false}
        itemNameCreateBtn="reports_create_button"
        itemNameDeleteBtn="reports_delete_button"
        itemNameEditEl="reports_edit_field"
        itemNameAddWidget="reports_add_widgets_button"
      />
      <ReportDownloadBar grid_id={grid_id} project={project} site={site} />
    </>
  ));
}

export default forwardRef(SiteReportPage);
